// navigation-completion Stage C/E — frontend API client for the per-document
// regional navigation ASSESSMENT.
//
// A SEPARATE module from v2Client.ts (which this phase does not edit); it reuses
// v2Client's exported `apiFetch` so auth headers, base URL and error handling are
// shared, never re-implemented. The wire types mirror the server result in
// src/services/navigation/documentAssessment.ts EXACTLY — the UI reads the server
// verdict (status, severity, company treatment, exemption disposition) and never
// re-states a regional rule or re-derives a pass.

import { apiFetch } from '@/api/v2Client';

export type NavigationAssessmentStatus = 'assessed' | 'unsupported' | 'corrupt' | 'conflict';
export type CriterionStatus = 'pass' | 'fail' | 'unknown' | 'not-evaluated' | 'not-applicable';
export type Severity = 'error' | 'warning' | 'recommendation';
export type AuthorityLevel = 'formal-validation' | 'guidance';
export type CompanyTreatment = 'must-fix' | 'advisory' | 'accept';

export interface AssessmentSourceWire {
  readonly authority: string;
  readonly specification: string;
  readonly version: string;
  readonly effectiveDate: string | null;
  readonly section: string;
  readonly url: string;
}

export type ExemptionDispositionWire =
  | { readonly kind: 'none' }
  | { readonly kind: 'exempted'; readonly exemptionId: string; readonly detail: string }
  | { readonly kind: 'rejected'; readonly exemptionId: string; readonly reason: string; readonly detail: string };

export interface AssessedCriterionWire {
  readonly criterionId: string;
  readonly profile: string | null;
  readonly category: string;
  readonly kind: 'regional' | 'structural-integrity';
  readonly status: CriterionStatus;
  readonly severity: Severity | null;
  readonly authorityLevel: AuthorityLevel | null;
  readonly source: AssessmentSourceWire | null;
  readonly enabled: boolean;
  readonly machineCheckable: boolean;
  readonly detail: string;
  readonly title: string;
  readonly appliesToLabel: string | null;
  readonly companyTreatment: CompanyTreatment | null;
  readonly exemption: ExemptionDispositionWire;
  readonly internalQuality: boolean;
  readonly waivable: boolean;
  readonly unknownReason: string | null;
}

export interface AssessedProfileWire {
  readonly profile: string;
  readonly requestedProfile: string;
  readonly role: 'target' | 'preparation';
  readonly evidenceLevel: string;
  readonly evaluated: boolean;
  readonly inheritsFrom: string | null;
  readonly advisory: string | null;
  readonly meta: {
    readonly label: string;
    readonly authority: string;
    readonly specification: string;
    readonly version: string;
    readonly effectiveDate: string | null;
    readonly url: string;
  } | null;
  readonly results: readonly AssessedCriterionWire[];
}

export interface NavigationAssessmentResult {
  readonly ok: true;
  readonly status: NavigationAssessmentStatus;
  readonly documentId: string;
  readonly submissionId: string;
  readonly region: string | null;
  readonly revision: number;
  readonly sourceSha256: string;
  readonly artifactSha256: string;
  readonly policyVersion: number;
  readonly evaluatorVersion: string;
  readonly source: {
    readonly assessedRevision: number;
    readonly isDerivative: boolean;
    readonly label: string;
  };
  readonly protection: {
    readonly state: string;
    readonly mutationAllowedByState: boolean;
    readonly detail: string;
  };
  readonly qualification: {
    readonly eligible: boolean;
    readonly reason: string;
  };
  readonly coverage: {
    readonly scanComplete: boolean | null;
    readonly remoteVerificationOmitted: boolean;
    readonly note: string;
  };
  readonly structural: readonly AssessedCriterionWire[];
  readonly profiles: readonly AssessedProfileWire[];
  readonly unsupportedReason: string | null;
  readonly detail: string | null;
  readonly targetScopeFingerprint?: string | null;
  readonly targetScopeDescription?: string;
  readonly exceptionBindingHash: string;
  readonly exceptionBindingMatchesAssessment: boolean;
}

export interface AssessmentPreconditions {
  readonly expectedRevision?: number;
  readonly expectedArtifactSha256?: string;
  readonly expectedPolicyVersion?: number;
}

function toQuery(pre?: AssessmentPreconditions): string {
  if (!pre) return '';
  const params = new URLSearchParams();
  if (pre.expectedRevision !== undefined) params.set('expectedRevision', String(pre.expectedRevision));
  if (pre.expectedArtifactSha256 !== undefined) params.set('expectedArtifactSha256', pre.expectedArtifactSha256);
  if (pre.expectedPolicyVersion !== undefined) params.set('expectedPolicyVersion', String(pre.expectedPolicyVersion));
  const q = params.toString();
  return q ? `?${q}` : '';
}

export const navigationAssessment = {
  get: (documentId: string, pre?: AssessmentPreconditions): Promise<NavigationAssessmentResult> =>
    apiFetch(
      `/api/v1/documents/${encodeURIComponent(documentId)}/navigation/assessment${toQuery(pre)}`,
    ),
};
